/**
 * UK-WHO / WHO 2006 Growth Chart utilities
 * LMS-based centile and z-score calculations for children 0-5 years
 *
 * Uses WHO 2006 Child Growth Standards (freely available)
 * UK-WHO combines WHO 2006 (0-4y) with UK90 (4-20y); this module implements WHO 2006 only.
 */

import { Gender } from '../types/patient'
import {
  WHO_WEIGHT_BOYS,
  WHO_WEIGHT_GIRLS,
  WHO_LENGTH_BOYS,
  WHO_LENGTH_GIRLS,
  WHO_BMI_BOYS,
  WHO_BMI_GIRLS,
  WHO_HEAD_BOYS,
  WHO_HEAD_GIRLS,
  type LmsRow,
  type Sex,
} from './who2006LmsData'

export type { Sex }

/** WHO 2006 covers 0-60 months (2 weeks to 5 years). Minimum 0.5 months (2 weeks) for most charts. */
const MIN_AGE_MONTHS = 0
const MAX_AGE_MONTHS = 60

/**
 * Calculate decimal age in years from two dates
 * Uses (observationDate - birthDate) / 365.25 for leap-year accuracy
 */
export function decimalAgeFromDates(dateOfBirth: string, observationDate: string): number {
  const birth = new Date(dateOfBirth)
  const obs = new Date(observationDate)
  const diffMs = obs.getTime() - birth.getTime()
  const diffDays = diffMs / (1000 * 60 * 60 * 24)
  return diffDays / 365.25
}

/**
 * Calculate age in months from date of birth to observation date
 */
export function ageInMonthsFromDates(dateOfBirth: string, observationDate: string): number {
  const birth = new Date(dateOfBirth)
  const obs = new Date(observationDate)
  return (obs.getFullYear() - birth.getFullYear()) * 12 + (obs.getMonth() - birth.getMonth())
}

/**
 * Convert measurement to z-score using LMS parameters
 * Z = (((X/M)^L) - 1) / (L*S) when L≠0
 * Z = ln(X/M)/S when L=0
 */
export function lmsToZScore(X: number, L: number, M: number, S: number): number {
  if (M <= 0 || X <= 0) return NaN
  if (Math.abs(L) < 1e-10) {
    return Math.log(X / M) / S
  }
  const term = Math.pow(X / M, L) - 1
  return term / (L * S)
}

/**
 * Convert z-score to centile (0-100) using standard normal CDF
 * Abramowitz & Stegun approximation (26.2.17)
 */
export function zScoreToCentile(z: number): number {
  if (z < -8) return 0
  if (z > 8) return 100

  const a1 = 0.254829592
  const a2 = -0.284496736
  const a3 = 1.421413741
  const a4 = -1.453152027
  const a5 = 1.061405429
  const p = 0.3275911

  const sign = z < 0 ? -1 : 1
  z = Math.abs(z) / Math.SQRT2

  const t = 1.0 / (1.0 + p * z)
  const y = 1.0 - ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-z * z)

  let cdf = 0.5 * (1.0 + sign * y)
  return Math.max(0, Math.min(100, cdf * 100))
}

/**
 * Interpolate LMS values for age between tabulated values
 */
function interpolateLms(
  ageMonths: number,
  data: Record<number, LmsRow>,
): LmsRow | null {
  const ages = Object.keys(data)
    .map(Number)
    .sort((a, b) => a - b)

  if (ageMonths < ages[0] || ageMonths > ages[ages.length - 1]) {
    return null
  }

  let lowerAge = ages[0]
  let upperAge = ages[ages.length - 1]

  for (let i = 0; i < ages.length - 1; i++) {
    if (ageMonths >= ages[i] && ageMonths <= ages[i + 1]) {
      lowerAge = ages[i]
      upperAge = ages[i + 1]
      break
    }
  }

  const lower = data[lowerAge]
  const upper = data[upperAge]

  if (lowerAge === upperAge) {
    return lower
  }

  const ratio = (ageMonths - lowerAge) / (upperAge - lowerAge)
  return {
    l: lower.l + (upper.l - lower.l) * ratio,
    m: lower.m + (upper.m - lower.m) * ratio,
    s: lower.s + (upper.s - lower.s) * ratio,
  }
}

function toSex(gender: Gender): Sex | null {
  if (gender === 'male') return 'male'
  if (gender === 'female') return 'female'
  return null
}

/**
 * Calculate weight centile (0-100) for a child using WHO 2006
 * Age: 0-60 months. Weight in kg.
 */
export function calculateWeightCentile(
  weightKg: number,
  dateOfBirth: string,
  observationDate: string,
  gender: Gender,
): number | null {
  const sex = toSex(gender)
  if (!sex) return null

  const ageMonths = ageInMonthsFromDates(dateOfBirth, observationDate)
  if (ageMonths < MIN_AGE_MONTHS || ageMonths > MAX_AGE_MONTHS) return null

  const data = sex === 'male' ? WHO_WEIGHT_BOYS : WHO_WEIGHT_GIRLS
  const lms = interpolateLms(ageMonths, data)
  if (!lms) return null

  const z = lmsToZScore(weightKg, lms.l, lms.m, lms.s)
  if (isNaN(z) || !isFinite(z)) return null

  return Math.round(zScoreToCentile(z) * 10) / 10
}

/**
 * Calculate height/length centile (0-100) for a child using WHO 2006
 * Age: 0-60 months. Height in cm.
 */
export function calculateHeightCentile(
  heightCm: number,
  dateOfBirth: string,
  observationDate: string,
  gender: Gender,
): number | null {
  const sex = toSex(gender)
  if (!sex) return null

  const ageMonths = ageInMonthsFromDates(dateOfBirth, observationDate)
  if (ageMonths < MIN_AGE_MONTHS || ageMonths > MAX_AGE_MONTHS) return null

  const data = sex === 'male' ? WHO_LENGTH_BOYS : WHO_LENGTH_GIRLS
  const lms = interpolateLms(ageMonths, data)
  if (!lms) return null

  const z = lmsToZScore(heightCm, lms.l, lms.m, lms.s)
  if (isNaN(z) || !isFinite(z)) return null

  return Math.round(zScoreToCentile(z) * 10) / 10
}

/**
 * Calculate BMI centile (0-100) for a child using WHO 2006
 * Age: 0-60 months.
 */
export function calculateBMICentile(
  bmi: number,
  dateOfBirth: string,
  observationDate: string,
  gender: Gender,
): number | null {
  const sex = toSex(gender)
  if (!sex) return null

  const ageMonths = ageInMonthsFromDates(dateOfBirth, observationDate)
  if (ageMonths < MIN_AGE_MONTHS || ageMonths > MAX_AGE_MONTHS) return null

  const data = sex === 'male' ? WHO_BMI_BOYS : WHO_BMI_GIRLS
  const lms = interpolateLms(ageMonths, data)
  if (!lms) return null

  const z = lmsToZScore(bmi, lms.l, lms.m, lms.s)
  if (isNaN(z) || !isFinite(z)) return null

  return Math.round(zScoreToCentile(z) * 10) / 10
}

/**
 * Calculate head circumference centile (0-100) for a child using WHO 2006
 * Age: 0-60 months. Head circumference in cm.
 */
export function calculateHeadCircumferenceCentile(
  ofcCm: number,
  dateOfBirth: string,
  observationDate: string,
  gender: Gender,
): number | null {
  const sex = toSex(gender)
  if (!sex) return null

  const ageMonths = ageInMonthsFromDates(dateOfBirth, observationDate)
  if (ageMonths < MIN_AGE_MONTHS || ageMonths > MAX_AGE_MONTHS) return null

  const data = sex === 'male' ? WHO_HEAD_BOYS : WHO_HEAD_GIRLS
  const lms = interpolateLms(ageMonths, data)
  if (!lms) return null

  const z = lmsToZScore(ofcCm, lms.l, lms.m, lms.s)
  if (isNaN(z) || !isFinite(z)) return null

  return Math.round(zScoreToCentile(z) * 10) / 10
}

/**
 * Get measurement value at a given z-score for chart plotting
 * X = M * (1 + L*S*Z)^(1/L) when L≠0
 * X = M * exp(S*Z) when L=0
 */
export function measurementFromZScore(z: number, L: number, M: number, S: number): number {
  if (Math.abs(L) < 1e-10) {
    return M * Math.exp(S * z)
  }
  const term = 1 + L * S * z
  if (term <= 0) return NaN
  return M * Math.pow(term, 1 / L)
}

/** Cole nine centiles: 0.4, 2, 9, 25, 50, 75, 91, 98, 99.6 - approximate z-scores */
export const COLE_CENTILE_Z_SCORES: number[] = [-2.67, -2, -1.33, -0.67, 0, 0.67, 1.33, 2, 2.67]

/** Standard percentiles for chart curves (3rd, 5th, 10th, 25th, 50th, 75th, 90th, 95th, 97th) */
export const STANDARD_PERCENTILE_Z_SCORES: number[] = [
  -1.881, -1.645, -1.282, -0.674, 0, 0.674, 1.282, 1.645, 1.881,
]

/** Percentile labels for chart axes */
export const PERCENTILE_LABELS = [3, 5, 10, 25, 50, 75, 90, 95, 97] as const

export type CentileCurvePoint = {
  ageMonths: number
  ageYears: number
  p3: number
  p5: number
  p10: number
  p25: number
  p50: number
  p75: number
  p90: number
  p95: number
  p97: number
}

/**
 * Generate centile curve data points for charting
 * Returns array suitable for Recharts (one row per age, columns per centile)
 */
export function generateCentileCurveData(
  chartType: 'weight' | 'height' | 'bmi' | 'headCircumference',
  sex: Sex,
  ageStepMonths: number = 1,
): CentileCurvePoint[] {
  const data =
    chartType === 'weight'
      ? sex === 'male'
        ? WHO_WEIGHT_BOYS
        : WHO_WEIGHT_GIRLS
      : chartType === 'height'
        ? sex === 'male'
          ? WHO_LENGTH_BOYS
          : WHO_LENGTH_GIRLS
        : chartType === 'bmi'
          ? sex === 'male'
            ? WHO_BMI_BOYS
            : WHO_BMI_GIRLS
          : sex === 'male'
            ? WHO_HEAD_BOYS
            : WHO_HEAD_GIRLS

  const result: CentileCurvePoint[] = []

  for (let ageMonths = 0; ageMonths <= MAX_AGE_MONTHS; ageMonths += ageStepMonths) {
    const lms = interpolateLms(ageMonths, data)
    if (!lms) continue

    const point: CentileCurvePoint = {
      ageMonths,
      ageYears: ageMonths / 12,
      p3: measurementFromZScore(STANDARD_PERCENTILE_Z_SCORES[0], lms.l, lms.m, lms.s),
      p5: measurementFromZScore(STANDARD_PERCENTILE_Z_SCORES[1], lms.l, lms.m, lms.s),
      p10: measurementFromZScore(STANDARD_PERCENTILE_Z_SCORES[2], lms.l, lms.m, lms.s),
      p25: measurementFromZScore(STANDARD_PERCENTILE_Z_SCORES[3], lms.l, lms.m, lms.s),
      p50: measurementFromZScore(STANDARD_PERCENTILE_Z_SCORES[4], lms.l, lms.m, lms.s),
      p75: measurementFromZScore(STANDARD_PERCENTILE_Z_SCORES[5], lms.l, lms.m, lms.s),
      p90: measurementFromZScore(STANDARD_PERCENTILE_Z_SCORES[6], lms.l, lms.m, lms.s),
      p95: measurementFromZScore(STANDARD_PERCENTILE_Z_SCORES[7], lms.l, lms.m, lms.s),
      p97: measurementFromZScore(STANDARD_PERCENTILE_Z_SCORES[8], lms.l, lms.m, lms.s),
    }
    result.push(point)
  }

  return result
}
